import type { CSSProperties, ReactElement, ReactNode } from "react";
import { CheckCircle2, Circle, ExternalLink, Lock } from "lucide-react";
import { Badge } from "@primitives/Badge";
import { IconButton } from "@primitives/IconButton";
import { Text } from "@primitives/Text";
import { useViewport } from "@primitives/viewport";
import { color, radius, space } from "@tokens/index";
import type { WorkItem } from "../../../experience-shell/workflows";
import { isWorkItemLockedAt } from "../workItemLock";
import { clampLines, leadingSlot, rowMain, rowMeta } from "./listSurface";
import { formatDueDate, relativeTime } from "./workItemTime";

/**
 * The content of a work-item row: a leading status glyph, the title, one
 * metadata line (type, due, lock, created-by, routing tags), and the trailing
 * presence and open-in-Galileo controls. The caller supplies the row
 * container. The queue-badge drill-in dialog reuses this so its rows match
 * the queue list.
 */

export interface WorkItemRowContentProps {
  readonly item: WorkItem;
  readonly now: Date;
  readonly onNavigateToGalileoEntity:
    | ((entityType: string, entityId: string) => void)
    | undefined;
  readonly renderPresence: ((item: WorkItem) => ReactNode) | undefined;
  /**
   * Dense list grammar: a `bodySm` title over one meta line with no block
   * padding, so a row settles at 44–48px. The default keeps the card
   * geometry the drill-in dialog uses.
   */
  readonly dense?: boolean;
  /** Class for the open-in-Galileo control, so the host can reveal it on hover. */
  readonly actionClassName?: string;
}

/** One wrapping line of chips. Tight gap so chips read as one group. */
const chipGroup: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: space[1],
  minWidth: 0,
  maxWidth: "100%",
};

/**
 * Compact metadata: chips, then the created line, then tags, each on its own
 * line. Three short lines read faster than six stacked chips.
 */
const compactMeta: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: space[1],
  minWidth: 0,
};

export function WorkItemRowContent({
  item,
  now,
  onNavigateToGalileoEntity,
  renderPresence,
  dense = false,
  actionClassName,
}: WorkItemRowContentProps): ReactElement {
  const { isCompact } = useViewport();
  const isCompleted = item.completedAt !== null;
  const tagEntries = Object.entries(item.routingTags);
  const dueLabel = item.dueDate !== null ? formatDueDate(item.dueDate, now) : null;
  const canLinkToGalileo =
    onNavigateToGalileoEntity !== undefined &&
    item.entityType.length > 0 &&
    item.entityId.length > 0;
  // Mirror the server read-time guard: an expired lock that is not cleared
  // yet is free, so a stale `lockedUntil` never renders the row as held.
  const isLocked = isWorkItemLockedAt(item, now);
  const lockHolder = item.lockedByDisplayName ?? item.lockedBy;
  const createdBy = item.createdByDisplayName ?? item.createdBy;
  const title = item.body.length > 0 ? item.body : (item.taskType ?? item.type);
  const presence = renderPresence !== undefined ? renderPresence(item) : null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: dense ? "center" : "flex-start",
        gap: space[2],
        width: "100%",
      }}
    >
      <span style={leadingSlot} aria-hidden="true">
        {isCompleted ? (
          <CheckCircle2 size={18} strokeWidth={2} color={color.accent.primary} />
        ) : (
          <Circle size={18} strokeWidth={2} color={color.fg.muted} />
        )}
      </span>

      <div style={{ ...rowMain, paddingBlock: dense ? 0 : isCompact ? space[1] : space[2] }}>
        <Text
          role={dense ? "bodySm" : "body"}
          weight="medium"
          {...(isCompact ? { style: clampLines(2) } : { truncate: true })}
        >
          {title}
        </Text>
        <div style={isCompact ? compactMeta : rowMeta}>
          <div style={chipGroup}>
            <Badge variant="accent" size="sm">
              {item.type}
            </Badge>
            {dueLabel !== null && (
              <Badge variant={dueLabel.variant} size="sm">
                {dueLabel.text}
              </Badge>
            )}
            {isCompleted && (
              <Badge variant="neutral" size="sm">
                COMPLETED
              </Badge>
            )}
            {isLocked && (
              <span
                role="status"
                aria-label={`Locked by ${lockHolder ?? "another user"}`}
                title={`Locked by ${lockHolder ?? "another user"}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: space[1],
                  paddingInline: space[2],
                  paddingBlock: space[1],
                  borderRadius: radius.pill,
                  background: color.bg.selected,
                  color: color.accent.primary,
                  maxWidth: 200,
                  minWidth: 0,
                }}
              >
                <Lock size={10} strokeWidth={2.25} aria-hidden="true" />
                <Text role="caption" weight="medium" truncate style={{ color: "inherit" }}>
                  {lockHolder ?? "Locked"}
                </Text>
              </span>
            )}
          </div>
          <Text role="caption" tone="muted" truncate>
            Created {relativeTime(item.createdAt, now)}
            {createdBy !== null && ` by ${createdBy}`}
          </Text>
          {tagEntries.length > 0 && (
            <div style={chipGroup}>
              {tagEntries.map(([key, value]) => (
                <Badge key={key} variant="neutral" size="sm" caps={false} truncate>
                  {`${key}: ${value}`}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {(presence !== null || canLinkToGalileo) && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: space[2],
            flexShrink: 0,
            minHeight: 40,
          }}
        >
          {presence}
          {canLinkToGalileo && (
            <IconButton
              className={actionClassName}
              icon={ExternalLink}
              size="sm"
              variant="ghost"
              aria-label={`Open ${item.entityType} ${item.entityId} in Galileo`}
              tooltip={`Open in Galileo: ${item.entityType} ${item.entityId}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onNavigateToGalileoEntity(item.entityType, item.entityId);
              }}
              onKeyDown={(e) => {
                // Enter and Space activate the link, not the row behind it.
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
            />
          )}
        </span>
      )}
    </div>
  );
}
